import React, { useRef, useEffect, useMemo } from "../../__compat__/react/main.entry.mjs.mjs";
import { useSpacing } from "../../api/spacing/useSpacing.mjs.mjs";
import { FormLabel } from "../FormLabel/index.mjs.mjs";
import { FormHelperText } from "../FormHelperText/index.mjs.mjs";
import { pickComponentCommonProps, pickAriaProps, pickDataProps, } from "../../interfaces/commonProps.mjs.mjs";
import { useLocale } from "../LocaleProvider/index.mjs.mjs";
import { StyledMinMax, StyledOutput, StyledSliderContainer, StyledContainer, StyledTrack, } from "./Slider.styles.mjs.mjs";
import { SliderThumb } from "./SliderThumb.mjs.mjs";
const noop = () => { };
const validateNumberOfThumbs = (numberOfThumbs) => numberOfThumbs === 1 || numberOfThumbs === 2;
export const Slider = React.forwardRef(({ 'aria-describedby': ariaDescribedBy = '', 'aria-labelledby': ariaLabelledBy, disabled, displayFormat: userDisplayFormat, id, hint, invalid, label, list, max, min, onChange = noop, onFocus = noop, onBlur = noop, step, validationMessage, value: unsanitizedValue, valueFormat, children, hiddenValues, rangeSeparator = 'to', ...restProps }, ref) => {
    const { localeIdentifier } = useLocale();
    const internalValueFormat = useMemo(() => valueFormat || new Intl.NumberFormat(localeIdentifier).format, [localeIdentifier, valueFormat]);
    const valuesArray = useMemo(() => Array.isArray(unsanitizedValue) ? unsanitizedValue : [unsanitizedValue], [unsanitizedValue]);
    const numberOfThumbs = valuesArray.length;
    if (!validateNumberOfThumbs(numberOfThumbs)) {
        throw new Error(`Expected to receive 1 or 2 thumbs, got ${numberOfThumbs}.`);
    }
    const containerRef = useRef(null);
    const outputRef = useRef(null);
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.setProperty('--value0', valuesArray[0].toString());
            if (numberOfThumbs === 2) {
                containerRef.current.style.setProperty('--value1', valuesArray[1].toString());
                containerRef.current.style.setProperty('--start', `var(${valuesArray[0] < valuesArray[1] ? '--position0' : '--position1'})`);
                containerRef.current.style.setProperty('--end', `var(${valuesArray[0] > valuesArray[1] ? '--position0' : '--position1'})`);
            }
            containerRef.current.style.setProperty('--min', min.toString());
            containerRef.current.style.setProperty('--max', max.toString());
        }
    }, [containerRef, valuesArray, numberOfThumbs, min, max]);
    const injectOutputSize = () => {
        if (containerRef.current && outputRef.current) {
            containerRef.current.style.setProperty('--output-size', `${outputRef.current.clientWidth}px`);
        }
    };
    useEffect(injectOutputSize, [
        valueFormat,
        containerRef,
        outputRef,
        valuesArray,
        min,
        max,
    ]);
    useEffect(() => {
        if (document.fonts)
            document.fonts.ready.then(injectOutputSize);
    }, []);
    const useExternalDataList = !Array.isArray(list);
    const labelId = `${id}__label`;
    const dataListId = useExternalDataList ? list : `${id}__datalist`;
    const hintMessageId = `${id}__hint-message`;
    const validationMessageId = `${id}__invalid-message`;
    const mergedDescribedBy = [
        hint ? hintMessageId : '',
        invalid ? validationMessageId : '',
        ariaDescribedBy,
    ]
        .join(' ')
        .trim();
    useMemo(() => {
        if (!label &&
            !restProps['aria-label'] &&
            !restProps['aria-labelledby']) {
            console.warn('It appears you may be missing a label. In the absence of the "label" prop, please provide an "aria-label" or "aria-labelledby" attribute to associate a label.');
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [label, restProps['aria-label'], restProps['aria-labelledby']]);
    const thumbChange = (index) => (event) => {
        const nextValue = parseFloat(event.target.value);
        if (numberOfThumbs === 2) {
            const arrayCopy = [...valuesArray];
            arrayCopy[index] = nextValue;
            onChange(arrayCopy, event);
        }
        else {
            onChange(nextValue, event);
        }
    };
    const locale = useLocale();
    const render = (propsArray) => propsArray.map((props) => React.createElement(SliderThumb, { key: props.id, ...props }));
    const displayFormat = userDisplayFormat ||
        (numberOfThumbs === 2
            ? ([start, end]) => locale.slider.selectedRange({ start, rangeSeparator, end })
            : (i) => i);
    return (React.createElement(StyledContainer, { outerSpacing: useSpacing(restProps), id: id, ref: ref, as: numberOfThumbs === 2 ? 'fieldset' : 'div', ...pickDataProps(restProps), ...pickAriaProps(restProps), ...pickComponentCommonProps(restProps), "aria-describedby": numberOfThumbs === 2 ? mergedDescribedBy : undefined },
        label && (React.createElement(FormLabel, { id: labelId, as: numberOfThumbs === 2 ? 'legend' : 'label', required: true, spaceStackEnd: "xxs" }, label)),
        !useExternalDataList && (React.createElement("datalist", { id: dataListId }, list.map((entry) => {
            const [value, label] = Array.isArray(entry) ? entry : [entry];
            return React.createElement("option", { key: value, value: value, label: label });
        }))),
        React.createElement(StyledSliderContainer, { ref: containerRef, numberOfThumbs: numberOfThumbs, disabled: disabled, invalid: invalid },
            React.createElement(StyledTrack, { numberOfThumbs: numberOfThumbs, marks: !useExternalDataList
                    ? list.map((value) => (Array.isArray(value) ? value[0] : value))
                    : [], disabled: disabled, invalid: invalid }),
            render(valuesArray.map((value, index) => ({
                'aria-valuemax': max,
                'aria-valuemin': min,
                'aria-valuenow': value,
                'aria-valuetext': internalValueFormat(value),
                ...(numberOfThumbs === 1 && {
                    'aria-labelledby': ariaLabelledBy || labelId,
                    'aria-describedby': mergedDescribedBy,
                }),
                ...(numberOfThumbs === 2 && {
                    ...(index === 0 && {
                        'aria-label': locale.slider.startAriaLabel,
                    }),
                    ...(index === 1 && {
                        'aria-label': locale.slider.endAriaLabel,
                    }),
                }),
                disabled,
                id: `${id}__thumb-${index}`,
                invalid,
                list: dataListId,
                max,
                min,
                onBlur,
                onChange: thumbChange(index),
                onFocus,
                step,
                value,
            }))),
            !hiddenValues && (React.createElement(React.Fragment, null,
                React.createElement(StyledOutput, { numberOfThumbs: numberOfThumbs, disabled: disabled, invalid: invalid, ref: outputRef }, displayFormat([...valuesArray]
                    .sort((a, b) => a - b)
                    .map(internalValueFormat))),
                React.createElement(StyledMinMax, { "aria-hidden": true },
                    React.createElement("span", null, internalValueFormat(min)),
                    React.createElement("span", null, internalValueFormat(max)))))),
        hint && (React.createElement(FormHelperText, { id: hintMessageId, spaceStackStart: 's', variant: "hint" }, hint)),
        invalid && (React.createElement(FormHelperText, { id: validationMessageId, spaceStackStart: hint ? 'xxs' : 's', variant: "invalid" }, validationMessage))));
});
Slider.displayName = 'Slider';
//# sourceMappingURL=Slider.mjs.mjs.map